import * as React from 'react'
import styled from 'styled-components'
import { AnimatePresence, motion } from 'motion/react'
import { Menu, Close, Person, Reply } from '@styled-icons/material'
import { FormsAppsTypes } from '@oneblink/types'
import { useAuth } from '@oneblink/apps-react'
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { FormsAppLogo } from './FormsAppLogo'
import { PageContent } from './LayoutComponents'
import { useHeaderButtonsContext } from '../providers/HeaderButtonsProvider'
import { useAppConfig } from '../providers/AppConfigProvider'
import { useHelpMessage } from '../providers/HelpMessageProvider'
import Dialog, { DialogHeading, DialogHeadingText } from '../components/Dialog'
import sanitizeHtml from '../lib/sanitize-html'
import clickSaveDraft from '../lib/click-save-draft'

import config from '../config'
import Masthead from './Masthead'
import { useFormsDefinitions } from '../providers/FormDefinitionsProvider'

const PageContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin: 0;
`
PageContainer.displayName = 'PageContainer'

const PageHeader = styled.header`
  border-bottom: 1px solid ${({ theme }) => theme.palette.nswPaletteGrey04};
`
PageHeader.displayName = 'PageHeader'

const HeaderContent = styled.div`
  max-width: var(--nsw-container-width);
  margin: 0 auto;
  flex: 1 1 auto;
  position: relative;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 1rem;

  @media screen and (${({ theme }) => theme.screenSizes.md}) {
    justify-content: unset;
    padding: 0 0.75rem;
  }
`
HeaderContent.displayName = 'HeaderContent'

export const Button = styled.button`
  background-color: ${({ theme }) => theme.palette.nswBrandLight};
  padding: 0.8em;
  color: ${({ theme }) => theme.palette.nswTextDark};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  &:active {
    background-color: ${({ theme }) => theme.palette.nswPaletteWhite};
    color: ${({ theme }) => theme.palette.nswBrandLight};
  }
`
Button.displayName = 'Button'

export const ButtonPrimary = styled(Button)`
  background-color: ${({ theme }) => theme.palette.nswBrandDark};
  color: ${({ theme }) => theme.palette.nswTextLight};
  &:active {
    background-color: ${({ theme }) => theme.palette.nswPaletteWhite};
    color: ${({ theme }) => theme.palette.nswBrandDark};
  }
`
ButtonPrimary.displayName = 'ButtonPrimary'

const PageBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  margin-bottom: 4rem;
`
PageBody.displayName = 'PageBody'

const PageFooter = styled.div`
  display: flex;
  flex: 0 1 auto;
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
  min-height: 4rem;
  position: relative;
  padding: 1rem 0;
`
PageFooter.displayName = 'PageFooter'

const FooterContent = styled.div`
  flex: 1 1 auto;
  flex-direction: row;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  max-width: 80%;
  margin: 0 auto;

  @media (${({ theme }) => theme.screenSizes.xs}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
`
FooterContent.displayName = 'FooterContent'

const FooterExternalLink = styled.a`
  color: ${({ theme }) => theme.palette.nswBrandDark};
  font-weight: bold;
  font-size: 0.7rem;
`

FooterExternalLink.displayName = 'FooterExternalLink'

const HelpLink = styled(FooterExternalLink)`
  position: absolute;
  right: 2rem;

  @media (${({ theme }) => theme.screenSizes.xs}) {
    position: relative;
    right: unset;
  }
`
HelpLink.displayName = 'HelpLink'

const LeftAlignText = styled.p`
  text-align: left;
  font-size: 0.7rem;
`

const PrivacyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-content: flex-end;
`
PrivacyContainer.displayName = 'PrivacyContainer'

const HeaderLogoContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.nswPaletteWhite};
`
HeaderLogoContainer.displayName = 'HeaderLogoContainer'

const CloseDialogButtonContainer = styled.div`
  padding: 1rem 0 0;
`
CloseDialogButtonContainer.displayName = 'CloseDialogButtonContainer'

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  right: 1rem;
  top: 1rem;
  padding-top: 0.75rem;
  min-height: 68px;

  @media screen and (${({ theme }) => theme.screenSizes.md}) {
    display: none;
  }
`
ButtonsContainer.displayName = 'ButtonsContainer'

const FAPrivacyPolicy = () => {
  return (
    <PrivacyContainer>
      <LeftAlignText>
        Your privacy is protected under the Privacy and Personal Information
        Protection Act 1998.
      </LeftAlignText>
      <LeftAlignText>
        Visit the{' '}
        <FooterExternalLink
          href="http://www.foodauthority.nsw.gov.au/gp/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy statement
        </FooterExternalLink>{' '}
        for more information about how the NSW Food Authority manages your
        personal information.
      </LeftAlignText>
    </PrivacyContainer>
  )
}
FAPrivacyPolicy.displayName = 'FAPrivacyPolicy'

const DPIPrivacyPolicy = () => {
  return (
    <PrivacyContainer>
      <LeftAlignText>
        Your privacy is protected under the Privacy and Personal Information
        Protection Act 1998.
      </LeftAlignText>
      <LeftAlignText>
        {' '}
        Visit the{' '}
        <FooterExternalLink
          href="https://www.regional.nsw.gov.au/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy statement
        </FooterExternalLink>{' '}
        for more information about how the Department manages your personal
        information.
      </LeftAlignText>
    </PrivacyContainer>
  )
}

DPIPrivacyPolicy.displayName = 'DPIPrivacyPolicy'

const HamburgerMenuContent = styled.div`
  right: 0;
  z-index: 3;
  padding: 16px 16px;
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
  box-shadow:
    rgb(0 0 0 / 20%) 0px 1px 5px 0px,
    rgb(0 0 0 / 14%) 0px 2px 2px 0px,
    rgb(0 0 0 / 12%) 0px 3px 1px -2px;
`
HamburgerMenuContent.displayName = 'HamburgerMenuContent'

export const HamburgerMenu = styled(function ({
  isOpen,
  children,
  onClick,
  ...props
}: React.PropsWithChildren<{
  isOpen: boolean
  className: string
  onClick: () => void
}>) {
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.section
          key="content"
          initial="collapsed"
          animate="open"
          exit="collapsed"
          variants={{
            open: { height: 'auto', overflowY: 'hidden' },
            collapsed: { height: 0, overflowY: 'hidden' },
          }}
          transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
        >
          <motion.div>
            <HamburgerMenuContent onClick={onClick} {...props}>
              {children}
            </HamburgerMenuContent>
          </motion.div>
        </motion.section>
      )}
    </AnimatePresence>
  )
})`
  display: none;

  @media screen and (${({ theme }) => theme.screenSizes.lg}) {
    display: block;
  }
`

const iconStyles = `
  display: flex;
  height: 24px;
  cursor: pointer;
`

const HamburgerIcon = styled(Menu)`
  ${iconStyles}
  flex: 0 0 auto;
  color: ${({ theme }) => theme.palette.nswBrandDark};
`
HamburgerIcon.displayName = 'HamburgerIcon'

const CloseIcon = styled(Close)`
  ${iconStyles}
  align-self: center;
  color: ${({ theme }) => theme.palette.nswBrandDark};
`
CloseIcon.displayName = 'CloseIcon'

function HamburgerMenuIcon({
  isOpen,
  ...props
}: {
  isOpen: boolean
  className: string
  onClick: () => void
}) {
  if (isOpen) {
    return <CloseIcon {...props} />
  }
  return <HamburgerIcon {...props} />
}

const HamburgerContainer = styled.div`
  flex: 0 1 auto;
  display: none;
  align-items: flex-start;
  position: absolute;
  padding-top: 0.75rem;
  flex-direction: column;
  align-items: center;
  font-weight: var(--nsw-font-bold);
  border-radius: var(--nsw-border-radius);
  color: var(--nsw-brand-dark);
  @media screen and (${({ theme }) => theme.screenSizes.md}) {
    display: flex;
  }
`
HamburgerContainer.displayName = 'HamburgerContainer'

const ProfileIconOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`
ProfileIconOption.displayName = 'ProfileIconOption'

const ProfileIcon = styled(Person)<{ $inHamburger: boolean }>`
  color: ${({ theme }) => theme.palette.nswBrandDark};
  height: ${({ $inHamburger }) => ($inHamburger ? '25px' : '2rem')};
  padding: ${({ $inHamburger }) => ($inHamburger ? '0' : '0 1rem')};
`
ProfileIcon.displayName = 'ProfileIcon'

const BackIcon = styled(Reply)<{ $visible: boolean; $inHamburger: boolean }>`
  color: ${({ theme }) => theme.palette.nswBrandDark};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  height: ${({ $inHamburger }) => ($inHamburger ? '25px' : '1.9rem')};
  padding: ${({ $inHamburger }) => ($inHamburger ? '0' : '0 1rem')};
  cursor: pointer;
`
BackIcon.displayName = 'BackIcon'

const HamburgerOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.palette.nswBrandDark};
  font-size: 1.2rem;
  padding: 0.8em;
  border-radius: 0.25rem;
  margin-top: 0.25rem;
  font-weight: 600;
  gap: 0.5rem;
  cursor: pointer;
  a {
    color: ${({ theme }) => theme.palette.nswBrandDark};
  }
  a:hover {
    color: ${({ theme }) => theme.palette.nswBrandDark};
  }
`

export function SaveDraftButton({
  saveDraft,
  buttons,
  className,
}: {
  saveDraft: () => void
  buttons: FormsAppsTypes.FormsListStyles['buttons']
  className: string
}) {
  return (
    <Button
      onClick={() => saveDraft()}
      className={className}
      style={{ width: '100%' }}
    >
      <span className="ob-customisable-button-inner">
        <span className="material-icons">
          {buttons?.saveDraft?.icon || 'save'}
        </span>
        {buttons?.saveDraft?.label || 'Save for later'}
      </span>
    </Button>
  )
}

const MenuText = styled.div`
  font-size: var(--nsw-font-size-xxs-mobile);
  flex: 0 0 auto;
`

export default function Page({
  children,
}: React.PropsWithChildren<Record<string, unknown>>) {
  const { isDraftButtonVisible } = useHeaderButtonsContext()
  const { buttons } = useAppConfig()
  const { isLoggedIn } = useAuth()
  const location = useLocation()
  const history = useHistory()
  const routeMatch = useRouteMatch<{ formId: string }>('/forms/:formId')
  const { helpMessage } = useHelpMessage()

  const [isShowingHelpDialog, setIsShowingHelpDialog] =
    React.useState<boolean>(false)
  const [isShowingHamburger, setIsShowingHamburger] = React.useState(false)

  const saveDraft = React.useCallback(() => {
    clickSaveDraft(() => setIsShowingHamburger(false))
  }, [])

  const { forms } = useFormsDefinitions()

  const customCSSClasses = React.useMemo(() => {
    if (forms && routeMatch) {
      const form = forms.find(
        (f) => f.id === parseInt(routeMatch.params.formId),
      )
      if (form) {
        return form.customCssClasses
      }
    }
  }, [forms, routeMatch])

  return (
    <PageContainer className={customCSSClasses?.join(' ') ?? ''}>
      <Masthead />
      <PageHeader>
        <HeaderContent>
          <HamburgerContainer className="fa-hamburger-container">
            <HamburgerMenuIcon
              isOpen={isShowingHamburger}
              className="fa-hamburger-toggle"
              onClick={() => setIsShowingHamburger(!isShowingHamburger)}
            />
            <MenuText>Menu</MenuText>
          </HamburgerContainer>
          <HeaderLogoContainer>
            <FormsAppLogo />
          </HeaderLogoContainer>
          <ButtonsContainer>
            {isDraftButtonVisible ? (
              <>
                <SaveDraftButton
                  buttons={buttons}
                  saveDraft={saveDraft}
                  className="fa-save-draft-button header-button"
                />
              </>
            ) : null}
            {location.pathname === '/me' && (
              <BackIcon
                className="header-button"
                $visible={location.pathname === '/me'}
                $inHamburger={false}
                onClick={() => history.goBack()}
              />
            )}
            {isLoggedIn && (
              <Link className="header-button" to="/me">
                <ProfileIcon $inHamburger={false} />
              </Link>
            )}
          </ButtonsContainer>
        </HeaderContent>
      </PageHeader>

      <HamburgerMenu
        isOpen={isShowingHamburger}
        className="fa-hamburger-menu"
        onClick={() => setIsShowingHamburger(false)}
      >
        {isDraftButtonVisible && (
          <SaveDraftButton
            buttons={buttons}
            saveDraft={saveDraft}
            className="fa-save-draft-button header-button"
          />
        )}
        {isLoggedIn && (
          <HamburgerOption>
            <Link to="/me">
              <ProfileIconOption>
                <ProfileIcon $inHamburger={true} />
                Profile
              </ProfileIconOption>
            </Link>
          </HamburgerOption>
        )}
        {location.pathname === '/me' && (
          <HamburgerOption onClick={() => history.goBack()}>
            <BackIcon
              className="header-button"
              $visible={location.pathname === '/me'}
              $inHamburger={true}
            />{' '}
            Back
          </HamburgerOption>
        )}
      </HamburgerMenu>

      <PageBody className="fa-page-body">
        <PageContent className="fa-page-body__content">{children}</PageContent>
      </PageBody>
      {helpMessage && (
        <Dialog
          isOpen={isShowingHelpDialog}
          onClose={() => setIsShowingHelpDialog(false)}
        >
          <DialogHeading>
            <DialogHeadingText>Help</DialogHeadingText>
          </DialogHeading>
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(helpMessage) }}
            className="ql-editor"
          />
          <CloseDialogButtonContainer>
            <ButtonPrimary onClick={() => setIsShowingHelpDialog(false)}>
              Close
            </ButtonPrimary>
          </CloseDialogButtonContainer>
        </Dialog>
      )}

      <PageFooter>
        <FooterContent>
          {config.CUSTOMER_NAME === 'NSW DPI' ? (
            <DPIPrivacyPolicy />
          ) : (
            <FAPrivacyPolicy />
          )}
          {helpMessage ? (
            <HelpLink onClick={() => setIsShowingHelpDialog(true)}>
              Help
            </HelpLink>
          ) : (
            <HelpLink
              href="https://www.foodauthority.nsw.gov.au/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Help
            </HelpLink>
          )}
        </FooterContent>
      </PageFooter>
    </PageContainer>
  )
}
